import { useCallback, useMemo } from 'react';
import { ApolloCache, ApolloError, useApolloClient } from '@apollo/client';
import { useTranslation } from 'react-i18next';

import {
  ContractConversationListDocument,
  ContractConversationListQuery,
  ContractMailboxOptionsQuery,
  ContractMailSummaryListQuery,
  ContractSellerOptionsQuery,
  ConversationThreadPageQuery,
  useContractConversationListQuery,
  useContractMailboxOptionsQuery,
  useContractMailSummaryListQuery,
  useContractSellerOptionsQuery,
  useContractSenderOptionsQuery,
  useConversationThreadPageQuery,
  usePostGmailReplyMutation,
  useWriteContractEmailMutation,
} from 'core/graphql';
import { GlobalToastTypeNames, ToastTypes } from 'types';

import { CONVERSATION_THREAD_PAGE } from '../Communications/useConversations';
import { appendThreadMessage } from '../Communications/threadCache';
import { IRichEmailPayload } from '../RichMail/useRichEmailDraft';
import { usePageVisiblePoll } from '../Meta/usePageVisiblePoll';
import { useToasts } from '../Toast/useToasts';
import { esignErrorCode } from './esignModel';
import { IEsignFieldError, toEsignErrors } from './useEsignMutations';
import { useEsignPermissions } from './useEsignPermissions';

/**
 * ТЗ 0078 — продавец договора и переписка с подписантом из карточки.
 *
 * Письма уходят из Gmail-ящика продавца (или общего), ответ приходит туда же
 * и зеркалится синхронизацией Gmail. Тред — обычный `ConversationType`
 * коммуникатора, поэтому лента треда читается ТЕМ ЖЕ запросом
 * `ConversationThreadPage` и той же страницей: у `conversationThread` в кэше
 * keyArgs — только `conversationId`, и страница другого размера писала бы в
 * тот же список (см. `CONVERSATION_THREAD_PAGE`).
 */

export type IContractSellerOption = ContractSellerOptionsQuery['contractSellers'][number];

export type IContractConversation = ContractConversationListQuery['contractConversations'][number];

export type IContractMailSummary = ContractMailSummaryListQuery['contractMailSummaries'][number];

export type IContractMailbox = ContractMailboxOptionsQuery['gmailMailboxes'][number];

type IThreadEdge = NonNullable<
  NonNullable<
    ConversationThreadPageQuery['conversationThread']['edges']
  >[number]
>;

export type IContractThreadMessage = NonNullable<IThreadEdge['node']>;

// Стабильные пустые массивы: новый `[]` на каждый рендер ломал бы memo.
const EMPTY_SELLERS: IContractSellerOption[] = [];
const EMPTY_CONVERSATIONS: IContractConversation[] = [];
const EMPTY_MAILBOXES: IContractMailbox[] = [];
const EMPTY_SUMMARIES: Readonly<Record<string, IContractMailSummary>> = {};

/**
 * Сводка почты по строкам списка перечитывается раз в минуту: ответ
 * подписанта приходит синхронизацией Gmail, не действием на этом экране.
 */
export const CONTRACT_MAIL_POLL_MS = 60000;

/**
 * Продавцы для фильтра списка (без флага) или для смены продавца
 * (`forReassign`, только Admin — остальным бэкенд отдаёт пустой список).
 */
export const useContractSellerOptions = (
  forReassign: boolean,
  enabled = true,
): { sellers: IContractSellerOption[]; loading: boolean } => {
  const { canReadStudent, isAdmin } = useEsignPermissions();
  const skip = !enabled || !canReadStudent || (forReassign && !isAdmin);
  const { data, loading } = useContractSellerOptionsQuery({
    variables: { forReassign },
    skip,
    fetchPolicy: 'cache-and-network',
  });
  return {
    sellers: data?.contractSellers ?? EMPTY_SELLERS,
    loading,
  };
};

/**
 * ТЗ 0083 — кто отправлял видимые договоры: варианты фильтра «Отправил».
 * Тот же тип строки, что у продавцов.
 */
export const useContractSenderOptions = (
  enabled = true,
): { senders: IContractSellerOption[]; loading: boolean } => {
  const { canReadStudent } = useEsignPermissions();
  const { data, loading } = useContractSenderOptionsQuery({
    skip: !enabled || !canReadStudent,
    fetchPolicy: 'cache-and-network',
  });
  return {
    senders: data?.contractSenders ?? EMPTY_SELLERS,
    loading,
  };
};

/**
 * Сводка почты для строк страницы — ОДИН запрос на страницу, как у SMS.
 * Ключ результата — id договора строкой.
 */
export const useContractMailSummaries = (
  contractIds: string[],
): Readonly<Record<string, IContractMailSummary>> => {
  const { canReadStudent } = useEsignPermissions();
  const idsKey = contractIds.join(',');
  const variables = useMemo(
    () => ({ contractIds: idsKey ? idsKey.split(',') : [] }),
    [idsKey],
  );
  const skip = !canReadStudent || variables.contractIds.length === 0;
  const {
    data,
    previousData,
    startPolling,
    stopPolling,
  } = useContractMailSummaryListQuery({
    variables,
    skip,
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: false,
  });
  usePageVisiblePoll(startPolling, stopPolling, CONTRACT_MAIL_POLL_MS, !skip);

  const rows = (data ?? previousData)?.contractMailSummaries;
  return useMemo(() => {
    if (!rows || rows.length === 0) return EMPTY_SUMMARIES;
    return rows.reduce<Record<string, IContractMailSummary>>((acc, row) => {
      acc[String(row.contractId)] = row;
      return acc;
    }, {});
  }, [rows]);
};

export interface IUseContractConversations {
  conversations: IContractConversation[];
  loading: boolean;
  error?: ApolloError;
  refetch: () => void;
}

/** Треды Gmail с подписантом договора (вкладка «Переписка»). */
export const useContractConversations = (
  contractId: string,
  enabled: boolean,
): IUseContractConversations => {
  const { canReadStudent } = useEsignPermissions();
  const skip = !enabled || !canReadStudent || !contractId;
  const {
    data,
    loading,
    error,
    refetch,
    startPolling,
    stopPolling,
  } = useContractConversationListQuery({
    variables: { contractId },
    skip,
    fetchPolicy: 'cache-and-network',
  });
  // Ответ подписанта появляется синхронизацией Gmail — пока вкладка открыта,
  // список раз в минуту перечитывается сам.
  usePageVisiblePoll(startPolling, stopPolling, CONTRACT_MAIL_POLL_MS, !skip);
  const reload = useCallback(() => {
    if (!skip) refetch().catch(() => undefined);
  }, [refetch, skip]);
  return {
    conversations: data?.contractConversations ?? EMPTY_CONVERSATIONS,
    loading,
    error,
    refetch: reload,
  };
};

export interface IUseContractThread {
  /** Сообщения старыми вверху, новыми внизу. */
  messages: IContractThreadMessage[];
  loading: boolean;
  hasOlder: boolean;
  /**
   * Догрузить более ранние. Возвращает промис — его оборачивает
   * `useMessengerScroll.keepAnchored`, чтобы вьюпорт остался на месте.
   */
  loadOlder: () => Promise<unknown>;
}

/** Лента одного треда: новейшая страница + «показать более ранние». */
export const useContractThread = (
  conversationId: string | null,
): IUseContractThread => {
  const { data, loading, fetchMore } = useConversationThreadPageQuery({
    variables: {
      conversationId: conversationId || '',
      first: CONVERSATION_THREAD_PAGE,
    },
    skip: !conversationId,
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
  });
  const connection = data?.conversationThread;
  const messages = useMemo(
    () =>
      (connection?.edges || [])
        .map((edge) => edge?.node)
        .filter((node): node is IContractThreadMessage => Boolean(node))
        .slice()
        .reverse(),
    [connection],
  );
  const cursor = connection?.pageInfo?.endCursor || null;
  const loadOlder = useCallback((): Promise<unknown> => {
    // `after: null` вернул бы ПЕРВУЮ страницу, и relay-merge склеил бы её
    // саму с собой.
    if (!cursor) return Promise.resolve();
    return fetchMore({
      variables: { after: cursor, first: CONVERSATION_THREAD_PAGE },
    });
  }, [cursor, fetchMore]);
  return {
    messages,
    loading,
    hasOlder: Boolean(connection?.pageInfo?.hasNextPage),
    loadOlder,
  };
};

/**
 * Ящики, с которых пользователь может написать (свой личный — первым).
 * `defaultEmail` — свой ящик, иначе первый из списка.
 */
export const useContractMailboxes = (
  enabled: boolean,
): {
  mailboxes: IContractMailbox[];
  defaultEmail: string;
  loading: boolean;
} => {
  const { data, loading } = useContractMailboxOptionsQuery({
    skip: !enabled,
    // Ящики — настройка сервера, меняются редко.
    fetchPolicy: 'cache-first',
  });
  const mailboxes = data?.gmailMailboxes ?? EMPTY_MAILBOXES;
  const defaultEmail =
    mailboxes.find((box) => box.isMine)?.email || mailboxes[0]?.email || '';
  return { mailboxes, defaultEmail, loading };
};

export interface IContractMailResult {
  ok: boolean;
  errors: IEsignFieldError[];
  conversationId: string | null;
}

interface IWritePayload {
  success?: boolean | null;
  customErrors?: Array<{
    field?: string | null;
    code?: string | null;
    message?: string | null;
  } | null> | null;
}

/**
 * Написать подписанту новое письмо / ответить в тред.
 *
 * Ошибки полей (`REQUIRED`, `NOT_YOUR_MAILBOX`, `MAIL_ERROR`…) возвращаются
 * форме — она рисует их у поля, коды приведены к верхнему регистру (ответ в
 * тред отдаёт их строчными). Отказ прав (промис отклонён) — тостом.
 */
export const useContractMailActions = (contractId: string) => {
  const { t } = useTranslation();
  const { addToast } = useToasts();
  const client = useApolloClient();
  const [writeEmail, { loading: writing }] = useWriteContractEmailMutation();
  const [postReply, { loading: replying }] = usePostGmailReplyMutation();

  const listRefetch = useMemo(
    () => [
      {
        query: ContractConversationListDocument,
        variables: { contractId },
      },
    ],
    [contractId],
  );

  const settle = useCallback(
    (
      payload: IWritePayload | null | undefined,
      conversationId: string | null,
    ): IContractMailResult => {
      const errors = toEsignErrors(payload?.customErrors).map((error) => ({
        ...error,
        code: esignErrorCode(error.code),
      }));
      return {
        ok: Boolean(payload?.success),
        errors,
        conversationId,
      };
    },
    [],
  );

  const crash = useCallback(
    (error: unknown): IContractMailResult => {
      addToast({
        data: {
          type: ToastTypes.error,
          title: t('esign.toast.failed'),
          text: error instanceof Error ? error.message : undefined,
        },
        type: GlobalToastTypeNames.Esign,
      });
      return { ok: false, errors: [], conversationId: null };
    },
    [addToast, t],
  );

  /**
   * Письмо сотрудника бэкенд пишет в журнал договора (`email_written`):
   * сбрасываем `events` карточки и сводку почты списка — оба перечитаются.
   */
  const touchContract = useCallback(
    (cache: ApolloCache<unknown>) => {
      cache.evict({
        id: cache.identify({
          __typename: 'ContractDocumentType',
          id: contractId,
        }),
        fieldName: 'events',
      });
      cache.evict({ id: 'ROOT_QUERY', fieldName: 'contractMailSummaries' });
      cache.gc();
    },
    [contractId],
  );

  const sendEmail = useCallback(
    async (input: {
      mailboxEmail: string;
      subject: string;
      /** ТЗ 0097: HTML письма (подстановки заполнит сервер) и вложения. */
      html: string;
      attachmentIds: string[];
      /** Шаблон, взятый за основу (ТЗ 0078) — для журнала. */
      templateId?: string | null;
    }): Promise<IContractMailResult> => {
      try {
        const { data } = await writeEmail({
          variables: {
            contractId,
            mailboxEmail: input.mailboxEmail,
            subject: input.subject,
            html: input.html,
            attachmentIds: input.attachmentIds,
            templateId: input.templateId || null,
          },
          refetchQueries: listRefetch,
          awaitRefetchQueries: true,
        });
        const payload = data?.sendContractEmail;
        const result = settle(payload, payload?.conversationId ?? null);
        if (result.ok) {
          touchContract(client.cache);
          addToast({
            data: {
              type: ToastTypes.success,
              title: t('esign.toast.mailSent'),
            },
            type: GlobalToastTypeNames.Esign,
          });
        }
        return result;
      } catch (error) {
        return crash(error);
      }
    },
    [
      addToast,
      client,
      contractId,
      crash,
      listRefetch,
      settle,
      t,
      touchContract,
      writeEmail,
    ],
  );

  /**
   * Ответ в тред. Тред НЕ перечитываем: `appendThreadMessage` дописывает
   * созданное сообщение в кэш сам (рефетч первой страницы схлопнул бы
   * догруженные ранние — ТЗ 0033). Список тредов перечитывается: у строки
   * меняется `lastMessageAt`.
   */
  const reply = useCallback(
    async (
      conversationId: string,
      body: IRichEmailPayload,
      /** Перепроверка F35: самое новое показанное входящее треда. */
      seenMessageId?: string | null,
    ): Promise<IContractMailResult> => {
      try {
        const { data } = await postReply({
          variables: {
            conversationId,
            html: body.html,
            attachmentIds: body.attachmentIds,
            seenMessageId: seenMessageId || null,
          },
          refetchQueries: listRefetch,
          update: (cache, res) => {
            const message = res.data?.sendGmailReply?.message;
            if (message?.id) {
              appendThreadMessage(
                cache,
                conversationId,
                message as { id: string } & Record<string, unknown>,
              );
            }
          },
        });
        const result = settle(data?.sendGmailReply, conversationId);
        if (result.ok) {
          touchContract(client.cache);
          addToast({
            data: {
              type: ToastTypes.success,
              title: t('esign.toast.replySent'),
            },
            type: GlobalToastTypeNames.Esign,
          });
        }
        return result;
      } catch (error) {
        return crash(error);
      }
    },
    [addToast, client, crash, listRefetch, postReply, settle, t, touchContract],
  );

  return { sendEmail, reply, busy: { writing, replying } };
};
